import { Banner, Input, Radio, Select, Tabs, Text } from '@cloudflare/kumo'
import { useTranslation } from '../../i18n'
import { formatWeekdayDay } from '../../lib/format'
import type { DayKind } from '../home/agenda'
import { NOW } from './visit-schedule'

export type When = 'booking' | 'other'
/** Value of the "Pick a date" tab (day tabs use their `YYYY-MM-DD`). */
export const PICK_DAY = 'pick'

export interface WhenSectionProps {
  when: When
  onWhenChange: (when: When) => void
  /** "With my booking" card; omitted when there is no booking to link to. */
  booking: { room: string; label: string } | null
  /** Upcoming bookings to choose from; a picker shows when there are several. */
  bookingOptions?: Array<{ id: string; label: string }>
  bookingId?: string
  onBookingIdChange?: (id: string) => void
  days: Array<{ ymd: string; kind: DayKind }>
  dayKey: string
  onDayKeyChange: (key: string) => void
  pickedYmd: string
  onPickedYmdChange: (ymd: string) => void
  minYmd: string
  maxYmd: string
  fromSlots: string[]
  fromValue: string
  onFromChange: (hm: string) => void
  untilOptions: string[]
  untilValue: string
  onUntilChange: (hm: string) => void
  /** Another day: the picked day has no bookable hours. */
  closed: boolean
  errorKey: string | null
  /** Staff day pass: fixed 06:00–22:00, so no booking choice and no From/Until. */
  dayPass?: boolean
}

/** "When": Radio cards (my booking / another day), then day tabs and From/Until selects (hidden for a day pass). */
export function WhenSection(props: WhenSectionProps) {
  const { t, locale } = useTranslation()
  const { when, booking, dayPass = false } = props

  const tabs = [
    ...props.days.map(({ ymd, kind }) => ({
      value: ymd,
      label:
        kind === 'today'
          ? t('dashboard.agenda.today')
          : kind === 'tomorrow'
            ? t('dashboard.agenda.tomorrow')
            : formatWeekdayDay(ymd, locale ?? 'pt-BR'),
      className: 'guest-tabs__tab',
    })),
    { value: PICK_DAY, label: t('dashboard.bookSheet.pickDate'), className: 'guest-tabs__tab' },
  ]

  return (
    <div className="guest-form__section">
      {booking && !dayPass ? (
        <Radio.Group
          legend={t('dashboard.bookSheet.when')}
          appearance="card"
          className="guest-radio"
          value={when}
          onValueChange={(v) => props.onWhenChange(v as When)}
        >
          <Radio.Item
            value="booking"
            label={t('dashboard.inviteSheet.withBooking')}
            description={`${booking.room} · ${booking.label}`}
          />
          <Radio.Item
            value="other"
            label={t('dashboard.inviteSheet.anotherTime')}
            description={t('dashboard.inviteSheet.anotherTimeHint')}
          />
        </Radio.Group>
      ) : (
        // No booking to link: a single radio card would be a choice of one.
        <Text as="h3" variant="secondary" DANGEROUS_className="font-medium">
          {t('dashboard.bookSheet.when')}
        </Text>
      )}

      {when === 'booking' && props.bookingOptions && props.bookingOptions.length > 1 && (
        <Select
          label={t('guestInvitations.form.whichBooking')}
          className="guest-control"
          value={props.bookingId}
          onValueChange={(v) => props.onBookingIdChange?.(String(v))}
        >
          {props.bookingOptions.map((o) => (
            <Select.Option key={o.id} value={o.id}>
              {o.label}
            </Select.Option>
          ))}
        </Select>
      )}

      {when === 'other' && (
        <>
          <Tabs
            className="guest-tabs"
            listClassName="guest-tabs__list"
            tabs={tabs}
            value={props.dayKey}
            onValueChange={(v) => props.onDayKeyChange(String(v))}
          />
          {props.dayKey === PICK_DAY && (
            <Input
              type="date"
              label={t('guestInvitations.form.visitDate')}
              className="guest-control"
              value={props.pickedYmd}
              min={props.minYmd}
              max={props.maxYmd}
              onChange={(e) => props.onPickedYmdChange(e.target.value)}
            />
          )}
          {props.closed ? (
            <Banner variant="alert">{t('dashboard.bookSheet.closed')}</Banner>
          ) : dayPass ? null : (
            <div className="guest-form__row">
              <Select
                label={t('guestInvitations.form.from')}
                className="guest-control"
                value={props.fromValue}
                renderValue={(v) => (v === NOW ? t('guestInvitations.form.fromNow') : String(v))}
                onValueChange={(v) => props.onFromChange(String(v))}
              >
                {props.fromSlots.map((hm) => (
                  <Select.Option key={hm} value={hm}>
                    {hm === NOW ? t('guestInvitations.form.fromNow') : hm}
                  </Select.Option>
                ))}
              </Select>
              <Select
                label={t('guestInvitations.form.until')}
                className="guest-control"
                value={props.untilValue}
                onValueChange={(v) => props.onUntilChange(String(v))}
              >
                {props.untilOptions.map((hm) => (
                  <Select.Option key={hm} value={hm}>
                    {hm}
                  </Select.Option>
                ))}
              </Select>
            </div>
          )}
        </>
      )}
      {props.errorKey && <Banner variant="error">{t(props.errorKey)}</Banner>}
    </div>
  )
}
